import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useWhatsApp } from "@/hooks/useWhatsApp";

interface WhatsAppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMessage?: string;
  defaultPhone?: string;
  onSend?: (phone: string, message: string) => Promise<void>;
  linkGenerator?: () => Promise<string>;
  obraId?: string;
  source?: 'export_btn' | 'popup' | 'contact_list';
}

export function WhatsAppModal({
  open,
  onOpenChange,
  defaultMessage = "",
  defaultPhone = "",
  onSend,
  linkGenerator,
  obraId,
  source = 'popup'
}: WhatsAppModalProps) {
  const [phone, setPhone] = useState(defaultPhone);
  const [message, setMessage] = useState(defaultMessage);
  
  const { openWhatsApp, openWhatsAppWithRDOLink, isLoading } = useWhatsApp();

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Por favor, insira uma mensagem.");
      return;
    }

    try {
      if (onSend) {
        // Callback customizado
        await onSend(phone, message);
      } else if (linkGenerator) {
        // Envio com link do RDO
        await openWhatsAppWithRDOLink(
          {
            phone: phone || undefined,
            baseText: message
          },
          linkGenerator,
          {
            obraId: obraId || '',
            date: new Date().toISOString(),
            hasPhone: !!phone,
            source
          }
        );
      } else {
        // Envio simples
        await openWhatsApp(
          {
            phone: phone || undefined,
            text: message
          },
          {
            obraId: obraId || '',
            date: new Date().toISOString(),
            hasPhone: !!phone,
            source
          }
        );
      }
      
      // Fechar modal e limpar campos
      onOpenChange(false);
      setPhone("");
      setMessage("");
    } catch (error) {
      console.error('Erro ao enviar WhatsApp:', error);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setPhone("");
    setMessage("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            Enviar via WhatsApp
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="whatsapp-phone">Telefone (opcional)</Label>
            <Input
              id="whatsapp-phone"
              placeholder="Ex: (11) 99999-9999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Deixe vazio para enviar sem número específico
            </p>
          </div>

          <div>
            <Label htmlFor="whatsapp-message">Mensagem</Label>
            <Textarea
              id="whatsapp-message"
              placeholder="Digite sua mensagem..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1 min-h-[120px]"
            />
            {linkGenerator && (
              <p className="text-xs text-muted-foreground mt-1">
                O link do relatório será adicionado automaticamente
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <Button 
            variant="outline" 
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSend}
            disabled={!message.trim() || isLoading}
            className="gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            {isLoading ? "Enviando..." : "Enviar WhatsApp"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}